import { FC, KeyboardEvent, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Select from '../Select';
import { ON_PRIMARY_FIELD_SX, ON_PRIMARY_OUTLINE_FIELD_SX } from '../fieldStyles';
import { colors } from '../../theme/tokens';

// XD multi-selection ("Patterns to respect" in DESIGN.md): dropdown +
// removable chips below. Used by "Service" and "Pour le compte de" in Add
// user, and by Recherche.
//
// Picking an option adds it to the list at once and empties the dropdown.
// The XD draws an orange ⊕ to confirm the pick, and that is how it worked:
// the dropdown only held a provisional choice. A service picked without ⊕
// looked chosen but was never sent, and accounts were created without one.
// The options already chosen are disabled in the list instead of
// disappearing, so that the user sees why they cannot choose them again.
// Designed for the blue background of the modals (white chips).
//
// With `freeText`, the dropdown is a text field, for what has no list
// (Recherche's "Émetteur" when the user is not an administrator and does not
// receive the accounts list). Typing has no moment where the entry is done,
// so there ⊕ (or Enter) adds it.
//
// With `readOnly` (the read-only user record) only the chips remain,
// without "✕" and without the dropdown; with none, "—".
//
// The mobile Recherche (smartphone XD, S7 in DESIGN.md) draws the chips
// ABOVE the dropdown and no ⊕: choosing an option adds it directly. That is
// `chipsAbove` + `addOnSelect`. In `freeText` the ⊕ stays even then: with no
// list there is nothing to "choose", and the button is the only visible way
// to add what was typed.

export type ChipOption = {
  value: string;
  label: string;
};

type Props = {
  /** Accessible name of the dropdown and base for ⊕'s ("Ajouter le service"). */
  label: string;
  placeholder: string;
  options: ChipOption[];
  value: string[];
  onChange: (value: string[]) => void;
  /** `name` attribute of the chips, for forms and tests. */
  name?: string;
  /** Text field instead of a dropdown; `options` only serves to label. */
  freeText?: boolean;
  /** `id` of the control, to link it with a label. */
  id?: string;
  /** Only the chips, without adding or removing. */
  readOnly?: boolean;
  /** Chips rendered above the control (mobile Recherche, XD S7). */
  chipsAbove?: boolean;
  /** Kept for callers; choosing an option now always adds it directly. */
  addOnSelect?: boolean;
  /** Transparent fields with a white outline (XD Recherche) instead of white-filled ones. */
  outlined?: boolean;
};

const MultiSelectChips: FC<Props> = ({
  label,
  placeholder,
  options,
  value,
  onChange,
  name,
  freeText = false,
  id,
  readOnly = false,
  chipsAbove = false,
  outlined = false,
}) => {
  const [draft, setDraft] = useState('');

  const add = () => {
    const item = draft.trim();
    if (!item || value.includes(item)) return;
    onChange([...value, item]);
    setDraft('');
  };

  const onTextKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      add();
    }
  };

  const remove = (item: string) => {
    onChange(value.filter((current) => current !== item));
  };

  const labelOf = (item: string) =>
    options.find((option) => option.value === item)?.label ?? item;

  const chipsEl = value.length > 0 && (
    <Box
      component="ul"
      aria-label={`${label} sélectionnés`}
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      {value.map((item) => (
        <li key={item}>
          <Chip
            label={labelOf(item)}
            variant="outlined"
            onDelete={readOnly ? undefined : () => remove(item)}
            data-name={name}
            data-value={item}
            sx={{
              color: colors.white,
              borderColor: colors.white,
              '& .MuiChip-deleteIcon': { color: colors.white },
            }}
          />
        </li>
      ))}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {readOnly && value.length === 0 && (
        <Typography sx={{ color: colors.white, py: 1 }}>—</Typography>
      )}

      {chipsAbove && chipsEl}

      {!readOnly && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            // The dropdown's intrinsic minimum is its placeholder at full
            // length (nowrap), which pushed the ⊕ past the dialog edge in
            // narrow columns. Let the field shrink — it ellipsizes — and
            // keep the ⊕ whole.
            '& > .MuiInputBase-root, & > .MuiFormControl-root': {
              flex: 1,
              minWidth: 0,
            },
            '& > span': { flexShrink: 0 },
          }}
        >
          {freeText ? (
            <TextField
              id={id}
              size="small"
              fullWidth
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onTextKeyDown}
              placeholder={placeholder}
              inputProps={{ 'aria-label': label }}
              sx={outlined ? ON_PRIMARY_OUTLINE_FIELD_SX : ON_PRIMARY_FIELD_SX}
            />
          ) : (
            <Select
              id={id}
              size="small"
              displayEmpty
              fullWidth
              value=""
              onChange={(e) => {
                const item = e.target.value as string;
                if (item && !value.includes(item)) onChange([...value, item]);
              }}
              renderValue={(selected: unknown) =>
                selected ? (
                  labelOf(String(selected))
                ) : (
                  <span
                    style={
                      outlined
                        ? { color: colors.white, opacity: 0.7 }
                        : { color: colors.hint }
                    }
                  >
                    {placeholder}
                  </span>
                )
              }
              inputProps={{ 'aria-label': label }}
              sx={outlined ? ON_PRIMARY_OUTLINE_FIELD_SX : ON_PRIMARY_FIELD_SX}
            >
              {options.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={value.includes(option.value)}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          )}
          {freeText && (
            <Tooltip title="Ajouter">
              {/* The span receives the tooltip when the button is disabled. */}
              <span>
                <IconButton
                  aria-label={`Ajouter ${label.toLowerCase()}`}
                  onClick={add}
                  disabled={!draft.trim()}
                  sx={{
                    color: colors.accent,
                    p: 0.5,
                    '&.Mui-disabled': { color: colors.accent, opacity: 0.5 },
                  }}
                >
                  <AddCircleOutlineIcon sx={{ fontSize: 32 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      )}

      {!chipsAbove && chipsEl}
    </Box>
  );
};

export default MultiSelectChips;
